import { ReactNode } from 'react';
import { useCurrentUser } from '@/hooks/useAuth';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LoginForm } from './LoginForm';
import type { UserRole } from '@/types/api.types';
import type { AssistantPermissions } from '@/types/entities';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
  /** If true, only admin users can access this page (assistants blocked even with permissions) */
  adminOnly?: boolean;
  /** Required permission page for assistants (e.g., 'menu', 'stock') */
  requiredPermission?: keyof AssistantPermissions;
  /** Multiple required permissions (OR logic - assistant needs at least one) */
  requiredPermissions?: (keyof AssistantPermissions)[];
}

export function ProtectedRoute({
  children,
  requiredRole,
  adminOnly = false,
  requiredPermission,
  requiredPermissions,
}: ProtectedRouteProps) {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { data: assistant, isLoading: isLoadingAssistant } = useCurrentAssistant();

  // Permission helper function
  const canViewPage = (page: keyof AssistantPermissions): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return Object.values(pagePermissions).some((val) => val === true);
  };

  const isAssistantAuthenticated = !!assistant;


  if (isLoadingUser || isLoadingAssistant) {
    return <LoadingSpinner fullScreen message="Authenticating..." />;
  }

  // Allow access if either admin user OR assistant is authenticated
  const isAuthenticated = !!user || isAssistantAuthenticated;

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <LoginForm />
      </div>
    );
  }


  // If adminOnly is set, block assistants completely (used for Settings page)
  if (adminOnly && assistant && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Check assistant permissions first (if assistant is logged in)
  if (assistant && !user) {
    // If requiredPermissions (array) is set, check if assistant has at least one (OR logic)
    if (requiredPermissions && requiredPermissions.length > 0) {
      const hasAnyPermission = requiredPermissions.some((perm) => canViewPage(perm));
      if (!hasAnyPermission) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
              <p className="text-muted-foreground">
                You don't have permission to access this page.
              </p>
            </div>
          </div>
        );
      }
    }
    // If requiredPermission (single) is set, check it
    else if (requiredPermission) {
      const hasPermission = canViewPage(requiredPermission);
      if (!hasPermission) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
              <p className="text-muted-foreground">
                You don't have permission to access this page.
              </p>
            </div>
          </div>
        );
      }
    }
    // If assistant has permission (or no permission check required), allow access
    return <>{children}</>;
  }

  // Check role if required (only for admin users, assistants use their own permission system)
  if (requiredRole && user && user.role !== requiredRole) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
